import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { Grid, GridItem } from '../../../components/Grid';
import {
  FormContainer,
  Form,
  Fieldset,
  Label,
  Dropdown,
  MessageBox,
  UpdateButton,
  ResetButton,
  NumberInput,
  TextInput,
} from '../../../components/FormElements';
import API from '../../../api';
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from '../../../constants';
import { getReturnUrl } from '../../../utils';

const AdminOperatorMarginUpdate = (props) => {
  const {
    appData: { accessType },
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [operators, setOperators] = useState([]);
  const [categories, setCategories] = useState([]);
  const [operatorsId, setOperatorsId] = useState('');
  const [categoriesId, setCategoriesId] = useState('');
  const [minMargin, setMinMargin] = useState('');
  const [maxMargin, setMaxMargin] = useState('');
  const [rofferMarginStartValue, setRofferMarginStartValue] = useState('');
  const [rofferMarginEndValue, setRofferMarginEndValue] = useState('');
  const [remark, setRemark] = useState('');
  const [originalFormData, setOriginalFormData] = useState({});
  const { id } = useParams();

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus('');
    setResponseMessage('');
  };

  const resetForm = () => {
    const {
      operatorsId,
      categoriesId,
      minMargin,
      maxMargin,
      rofferMarginStartValue,
      rofferMarginEndValue,
      remark,
    } = originalFormData;
    setOperatorsId(operatorsId);
    setCategoriesId(categoriesId);
    setMinMargin(minMargin);
    setMaxMargin(maxMargin);
    setRofferMarginStartValue(rofferMarginStartValue);
    setRofferMarginEndValue(rofferMarginEndValue);
    setRemark(remark);
  };

  const readOperators = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(
      '/recharge-configurations/operators?pageNumber=1&recordsPerPage=100000'
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setOperators(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const readCategories = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(
      `/recharge-configurations/categories/?pageNumber=1&recordsPerPage=100000`
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setCategories(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const readData = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(`/recharge-configurations/admin-operator-margins/${id}`)
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          const {
            operatorsId,
            categoriesId,
            minMargin,
            maxMargin,
            rofferMarginStartValue,
            rofferMarginEndValue,
            remark,
          } = data[0];
          setOperatorsId(operatorsId);
          setCategoriesId(categoriesId);
          setMinMargin(minMargin);
          setMaxMargin(maxMargin);
          setRofferMarginStartValue(rofferMarginStartValue);
          setRofferMarginEndValue(rofferMarginEndValue);
          setRemark(remark);
          setOriginalFormData({
            operatorsId,
            categoriesId,
            minMargin,
            maxMargin,
            rofferMarginStartValue,
            rofferMarginEndValue,
            remark,
          });
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  const onSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    const formData = {
      operatorsId,
      categoriesId,
      minMargin,
      maxMargin,
      rofferMarginStartValue: ['V1', 'V3'].includes(accessType)
        ? rofferMarginStartValue
        : 0,
      rofferMarginEndValue: ['V1', 'V3'].includes(accessType)
        ? rofferMarginEndValue
        : 0,
      remark,
    };
    API.put(`/recharge-configurations/admin-operator-margins/${id}`, formData)
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setOriginalFormData(formData);
          setTimeout(() => {
            resetResponseData();
            window.location.href = getReturnUrl(PERMISSION_TYPES.UPDATE);
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    readData();
    readOperators();
    readCategories();
  }, [readData, readOperators, readCategories]);

  const operatorsOptions = [{ title: '-- SELECT OPERATOR --', value: '' }];
  operators.forEach(({ id, operatorName }) =>
    operatorsOptions.push({
      title: operatorName,
      value: id,
    })
  );

  const categoriesOptions = [{ title: '-- SELECT CATEGORY --', value: '' }];
  categories.forEach(({ id, category }) =>
    categoriesOptions.push({
      title: category,
      value: id,
    })
  );

  return (
    <>
      <PageHeading {...props} />
      <FormContainer>
        <Form method="POST" action="#" onSubmit={onSubmit}>
          <Grid
            columns={
              'calc(25% - 15px) calc(25% - 15px) calc(25% - 15px) calc(25% - 15px)'
            }
          >
            <GridItem>
              <Fieldset>
                <Label required>Operator</Label>
                <Dropdown
                  placeholder="Please select operator"
                  value={operatorsId}
                  onChange={setOperatorsId}
                  options={operatorsOptions}
                  disabled={isLoading}
                  searchable
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Category</Label>
                <Dropdown
                  placeholder="Please select category"
                  value={categoriesId}
                  onChange={setCategoriesId}
                  options={categoriesOptions}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Min Margin</Label>
                <NumberInput
                  value={minMargin}
                  onChange={setMinMargin}
                  placeholder="Please enter min margin"
                  maxLength={100}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Max Margin</Label>
                <NumberInput
                  value={maxMargin}
                  onChange={setMaxMargin}
                  placeholder="Please enter max margin"
                  maxLength={100}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          {['V1', 'V3'].includes(accessType) && (
            <Grid
              columns={
                'calc(33.33% - 13.33px) calc(33.33% - 13.33px) calc(33.33% - 13.33px)'
              }
            >
              <GridItem>
                <Fieldset>
                  <Label required>Roffer Margin Start Value</Label>
                  <NumberInput
                    value={rofferMarginStartValue}
                    onChange={setRofferMarginStartValue}
                    placeholder="Please enter roffer margin start value"
                    maxLength={100}
                    disabled={isLoading}
                  />
                </Fieldset>
              </GridItem>
              <GridItem>
                <Fieldset>
                  <Label required>Roffer Margin End Value</Label>
                  <NumberInput
                    value={rofferMarginEndValue}
                    onChange={setRofferMarginEndValue}
                    placeholder="Please enter roffer margin end value"
                    maxLength={100}
                    disabled={isLoading}
                  />
                </Fieldset>
              </GridItem>
              <GridItem>
                <Fieldset>
                  <Label>Remark</Label>
                  <TextInput
                    value={remark}
                    onChange={setRemark}
                    placeholder="Please enter remark"
                    maxLength={1000}
                    disabled={isLoading}
                  />
                </Fieldset>
              </GridItem>
            </Grid>
          )}
          {responseStatus && (
            <Fieldset>
              <MessageBox status={responseStatus} message={responseMessage} />
            </Fieldset>
          )}
          <Fieldset>
            <UpdateButton disabled={isLoading} type="submit">
              Update
            </UpdateButton>
            <ResetButton disabled={isLoading} type="button" onClick={resetForm}>
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
        <OverlayLoader showLoader={isLoading} />
      </FormContainer>
    </>
  );
};

export default withAppHOC(AdminOperatorMarginUpdate);
